import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { fetchAp2025PharmacyColleges } from "../api/getApi";
import ShareModal from "../components/shareModal";

const AP2025PharmacyColleges = () => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regionFilter, setRegionFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [uniqueRegions, setUniqueRegions] = useState([]);
  const [uniqueTypes, setUniqueTypes] = useState([]);
  const [uniqueBranches, setUniqueBranches] = useState([]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  
  const [sortConfig, setSortConfig] = useState({ key: "inst_name", direction: 'ascending' });

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareTextVisible, setIsShareTextVisible] = useState(false);

  // Effect to show and hide share text
  useEffect(() => {
    const timer1 = setTimeout(() => {
      setIsShareTextVisible(true);
    }, 300);
    const timer = setTimeout(() => {
      setIsShareTextVisible(false);
    }, 2500);
    return () => {
      clearTimeout(timer);
      clearTimeout(timer1);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const currentPageUrl = window.location.href;

  useEffect(() => {
    const loadColleges = async () => {
      try {
        setLoading(true);
        setError(null);
        const collegeData = await fetchAp2025PharmacyColleges();
        
        setColleges(collegeData);
        
        const regions = [...new Set(collegeData.map(college => college.region))].filter(Boolean).sort();
        const types = [...new Set(collegeData.map(college => college.type))].filter(Boolean).sort();
        const branches = [...new Set(collegeData.map(college => college.branch_code))].filter(Boolean).sort();
        
        setUniqueRegions(regions);
        setUniqueTypes(types);
        setUniqueBranches(branches);
      } catch (err) {
        setError("Error fetching data: " + err.message);
        console.error("Error fetching AP 2025 pharmacy colleges:", err);
      } finally {
        setLoading(false);
      }
    };

    loadColleges();
  }, []);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const filteredColleges = useMemo(() => {
    let filteredData = colleges.filter(college => {
      if (regionFilter !== "all" && college.region !== regionFilter) return false;
      if (typeFilter !== "all" && college.type !== typeFilter) return false;
      if (branchFilter !== "all" && college.branch_code !== branchFilter) return false;
      if (searchTerm) {
        const searchTermLower = searchTerm.toLowerCase();
        return (
          college.inst_name.toLowerCase().includes(searchTermLower) ||
          college.inst_code.toLowerCase().includes(searchTermLower) ||
          college.place.toLowerCase().includes(searchTermLower) ||
          college.branch_code.toLowerCase().includes(searchTermLower) ||
          college.dist.toLowerCase().includes(searchTermLower)
        );
      }
      return true;
    });

    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        if (typeof a[sortConfig.key] === 'number') {
          return sortConfig.direction === 'ascending' 
            ? a[sortConfig.key] - b[sortConfig.key]
            : b[sortConfig.key] - a[sortConfig.key];
        }
        
        const valA = String(a[sortConfig.key] || '').toLowerCase();
        const valB = String(b[sortConfig.key] || '').toLowerCase();

        if (valA < valB) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (valA > valB) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return filteredData;
  }, [colleges, regionFilter, typeFilter, branchFilter, searchTerm, sortConfig]);

  const totalPages = Math.ceil(filteredColleges.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredColleges.slice(indexOfFirstItem, indexOfLastItem);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    
    return (
      <span className="ml-1">
        {sortConfig.direction === 'ascending' ? (
          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </span>
    );
  };

  const resetFilters = () => {
    setSearchTerm("");
    setRegionFilter("all");
    setTypeFilter("all");
    setBranchFilter("all");
    setSortConfig({ key: "inst_name", direction: 'ascending' });
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500"></div>
        <p className="mt-4 text-lg text-gray-700">Loading AP 2025 Pharmacy colleges data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-lg max-w-lg text-center">
          <p className="font-bold mb-2">Error Loading Data</p>
          <p>{error}</p>
          <button 
            onClick={() => {
              setLoading(true);
              setError(null);
              window.location.reload(); 
            }}
            className="mt-4 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <Helmet>
        <title>AP 2025 Pharmacy Colleges | CollegeRank</title>
        <meta name="description" content="Complete list of AP 2025 pharmacy colleges with details on fees, courses, and seat availability." />
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="mb-4">
          <Link
            to="/"
            className="text-purple-600 hover:underline font-medium flex items-center gap-1"
          >
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Home
          </Link>
        </div>
        
        <div className="bg-white shadow-md rounded-lg">
          <div className="p-6 border-b flex justify-between items-start sm:items-center relative">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">AP 2025 Pharmacy Colleges</h1>
              <p className="text-gray-600 mt-1">
                Complete list of {colleges.length > 0 ? colleges.length : '...'} pharmacy colleges in Andhra Pradesh for the year 2025.
              </p>
            </div>
            <div
              className="relative flex flex-col items-end"
              onMouseEnter={() => setIsShareTextVisible(true)}
              onMouseLeave={() => setIsShareTextVisible(false)}
            >
              <div
                className={`
                  absolute top-[-2.5rem] right-0 bg-purple-300 text-gray-800 px-4 py-2 rounded-lg shadow-lg 
                  text-sm font-medium transition-all duration-300 ease-in-out whitespace-nowrap z-50
                  ${isShareTextVisible ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95"}
                `}
              >
                Share With Friends
              </div>
              <button
                onClick={() => setIsShareModalOpen(true)}
                className="ml-4 bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2 px-3 sm:px-4 rounded-lg flex items-center shadow-sm transition-colors text-sm"
                aria-label="Share this page"
              >
                <svg
                  className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6.001l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                  ></path>
                </svg>
                <span className="hidden sm:inline">Share</span>
              </button>
            </div>
          </div>

          <div className="p-6 border-b bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                  Search
                </label>
                <input
                  type="text"
                  id="search"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); 
                  }}
                  placeholder="Search by college, branch, location..."
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 px-4 py-2 border"
                />
              </div>
              <div>
                <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">
                  Region
                </label>
                <select
                  id="region"
                  value={regionFilter}
                  onChange={(e) => {
                    setRegionFilter(e.target.value);
                    setCurrentPage(1); 
                  }}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 px-4 py-2 border"
                >
                  <option value="all">All Regions</option>
                  {uniqueRegions.map((region) => (
                    <option key={region} value={region}>
                      {region}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                  Institution Type
                </label>
                <select
                  id="type"
                  value={typeFilter}
                  onChange={(e) => {
                    setTypeFilter(e.target.value);
                    setCurrentPage(1); 
                  }}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 px-4 py-2 border"
                >
                  <option value="all">All Types</option>
                  {uniqueTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="branch" className="block text-sm font-medium text-gray-700 mb-1">
                  Branch
                </label>
                <select
                  id="branch"
                  value={branchFilter}
                  onChange={(e) => {
                    setBranchFilter(e.target.value);
                    setCurrentPage(1); 
                  }}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 px-4 py-2 border"
                >
                  <option value="all">All Branches</option>
                  {uniqueBranches.map((branch) => (
                    <option key={branch} value={branch}>
                      {branch}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {(searchTerm || regionFilter !== "all" || typeFilter !== "all" || branchFilter !== "all") && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={resetFilters}
                  className="px-3 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded"
                >
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          <div className="px-6 py-3 bg-gray-100 border-b">
            <div className="flex flex-col sm:flex-row justify-between items-center">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{filteredColleges.length}</span> results
              </p>
              <div className="flex items-center mt-2 sm:mt-0">
                <label htmlFor="page-size" className="mr-2 text-sm text-gray-700">
                  Show:
                </label>
                <select
                  id="page-size"
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1); 
                  }}
                  className="rounded border-gray-300 text-sm py-1 px-2 border"
                >
                  {[25, 50, 100, 250].map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {filteredColleges.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('inst_name')}
                    >
                      <div className="flex items-center">
                        College {getSortDirectionIndicator('inst_name')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('branch_code')}
                    >
                      <div className="flex items-center">
                        Branch {getSortDirectionIndicator('branch_code')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('place')}
                    >
                      <div className="flex items-center">
                        Location {getSortDirectionIndicator('place')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hidden md:table-cell"
                      onClick={() => requestSort('region')}
                    >
                      <div className="flex items-center">
                        Region {getSortDirectionIndicator('region')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hidden md:table-cell"
                      onClick={() => requestSort('type')}
                    >
                      <div className="flex items-center">
                        Type {getSortDirectionIndicator('type')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hidden lg:table-cell"
                      onClick={() => requestSort('affilia_univ')}
                    >
                      <div className="flex items-center">
                        University {getSortDirectionIndicator('affilia_univ')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('college_fee')}
                    >
                      <div className="flex items-center justify-end">
                        Fee (₹) {getSortDirectionIndicator('college_fee')}
                      </div>
                    </th>
                    <th 
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                      onClick={() => requestSort('convener_seats')}
                    >
                      <div className="flex items-center justify-end">
                        Seats {getSortDirectionIndicator('convener_seats')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentItems.map((college) => (
                    <tr key={college._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-purple-600">{college.inst_name}</div>
                        <div className="text-xs text-gray-500">{college.inst_code}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {college.branch_code}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{college.place}</div>
                        <div className="text-xs text-gray-500">{college.dist}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden md:table-cell">
                        {college.region}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden md:table-cell">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          college.type === 'GOVT' ? 'bg-green-100 text-green-800' : 
                          college.type === 'PVT' ? 'bg-purple-100 text-purple-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {college.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden lg:table-cell">
                        {college.affilia_univ}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {college.college_fee ? `₹${college.college_fee.toLocaleString()}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {college.convener_seats}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="py-8 text-center">
                <p className="text-gray-500">No colleges found matching your criteria.</p>
                <button 
                  onClick={resetFilters}
                  className="mt-4 text-purple-600 hover:underline"
                >
                  Reset filters
                </button>
              </div>
            )}
          </div>

          {filteredColleges.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t">
              <div className="flex flex-col sm:flex-row items-center justify-between">
                <div className="flex items-center mb-4 sm:mb-0">
                  <span className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
                    <span className="font-medium">
                      {Math.min(indexOfLastItem, filteredColleges.length)}
                    </span>{" "}
                    of <span className="font-medium">{filteredColleges.length}</span> results
                  </span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => goToPage(1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded text-sm ${
                      currentPage > 1
                        ? "bg-purple-600 text-white hover:bg-purple-700"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    First
                  </button>
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded text-sm ${
                      currentPage > 1
                        ? "bg-purple-600 text-white hover:bg-purple-700"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Previous
                  </button>
                  <div className="flex items-center px-2">
                    <span className="text-sm text-gray-700">
                      Page <span className="font-medium">{currentPage}</span> of{" "}
                      <span className="font-medium">{totalPages}</span>
                    </span>
                  </div>
                  <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded text-sm ${
                      currentPage < totalPages
                        ? "bg-purple-600 text-white hover:bg-purple-700"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => goToPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded text-sm ${
                      currentPage < totalPages
                        ? "bg-purple-600 text-white hover:bg-purple-700"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    Last
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">About AP 2025 Pharmacy Colleges</h2>
            <p className="text-gray-600">
              This page provides a comprehensive list of pharmacy colleges in Andhra Pradesh for the year 2025.
              You can filter by region, institution type, and branch to find the colleges that match your requirements.
            </p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">How to Use This Data</h2>
            <p className="text-gray-600">
              Use this data for reference during counseling. The list includes college names, locations, branches, fees, and seat availability.
              Click on column headers to sort the data. Use the filters to narrow down your search based on specific criteria.
            </p>
          </div>
        </div>
      </div>
      <ShareModal
        open={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        link={currentPageUrl}
      />
    </div>
  );
};

export default AP2025PharmacyColleges;